// Onboarding tour overlay. Walks new (anonymous) visitors through the app
// with a spotlight + tooltip on each major surface. Mobile-first: tooltip
// auto-positions to stay on-screen at narrow viewports (390px wide
// reference). No external deps.
//
// Public API:
//   startTour(navHooks)         — kick off; navHooks = { switchTab, setLibTab }
//   isTourPending()             — true if user has never completed/skipped
//   markTourCompleted()         — record completion in localStorage

const TOUR_FLAG_KEY = 'sayermore.tourCompleted';

let state = null;

export function isTourPending() {
  try {
    return localStorage.getItem(TOUR_FLAG_KEY) !== '1';
  } catch {
    return false;
  }
}

export function markTourCompleted() {
  try { localStorage.setItem(TOUR_FLAG_KEY, '1'); } catch {}
}

// Steps reference DOM IDs added in index.html. `before` lets us drive the
// app into the right view before measuring the spotlight (e.g. switch to
// the Library tab before highlighting a sub-tab).
function buildSteps() {
  return [
    {
      id: 'welcome',
      target: null,
      title: "Let's take a quick tour",
      body: "Sayermore keeps your thoughts, photos, and the stories behind them. Explore the sample library here; sign in for photo memories and Ask.",
    },
    {
      id: 'capture-tab',
      target: '#captureTabBtn',
      title: 'This is where you talk',
      body: 'Catch an idea, keep a great find, or remember why. Pick a starting point if it helps, then choose Audio clip or Photo.',
      before: (h) => h.switchTab('capture'),
    },
    {
      id: 'photo', target: '#choosePhotoBtn', title: 'Keep a photo and its story',
      body: 'Sign in to save a photo with a typed or recorded explanation. Originals stay on this device first; the synced description becomes searchable.',
      before: (h) => h.switchTab('capture'),
    },
    {
      id: 'library-tab',
      target: '#libraryTabBtn',
      title: 'Your library',
      body: 'Everything you say becomes searchable here. Take a look — we seeded a few sample folders so you can see the shape of it.',
      before: (h) => h.switchTab('library'),
    },
    {
      id: 'organized',
      target: '#organizedTabBtn',
      title: 'Notes',
      body: "Captures get synthesized into a growing library. Related thoughts append to existing notes over time — no folder management on your end.",
      before: (h) => { h.switchTab('library'); h.setLibTab('organized'); },
    },
    {
      id: 'sessions',
      target: '#sessionsTabBtn',
      title: 'Sessions',
      body: 'Find the original source behind your notes here. Photo memories keep images and their voice explanation on the device where you save them.',
      before: (h) => { h.switchTab('library'); h.setLibTab('sessions'); },
    },
    {
      id: 'auth',
      target: '#authBtn',
      title: "When you're ready",
      body: 'Sign in to save your library across devices. The captures you make during the demo come with you — you choose which to keep.',
      before: (h) => h.switchTab('library'),
    },
  ];
}

export function startTour(navHooks = {}) {
  if (state) return; // already running
  const steps = buildSteps();
  state = {
    steps,
    index: 0,
    navHooks: {
      switchTab: navHooks.switchTab || (() => {}),
      setLibTab: navHooks.setLibTab || (() => {}),
    },
    overlay: null,
  };
  mountOverlay();
  renderStep();
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onResize, true);
}

function mountOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'tour-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.innerHTML = `
    <div class="tour-spotlight" id="tourSpotlight"></div>
    <div class="tour-card" id="tourCard">
      <div class="tour-step-counter" id="tourCounter"></div>
      <h3 id="tourTitle"></h3>
      <p id="tourBody"></p>
      <div class="tour-actions">
        <button class="tour-skip" id="tourSkip">Skip</button>
        <button class="tour-next pill primary" id="tourNext">Next</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  state.overlay = overlay;
  overlay.querySelector('#tourNext').addEventListener('click', next);
  overlay.querySelector('#tourSkip').addEventListener('click', end);
}

function renderStep() {
  if (!state) return;
  const step = state.steps[state.index];
  if (!step) { end(); return; }
  try {
    if (step.before) step.before(state.navHooks);
  } catch (e) {
    console.warn('[tour] before-hook failed', e);
  }
  // Allow the app to render after navigation before measuring.
  requestAnimationFrame(() => requestAnimationFrame(() => positionStep(step)));
}

function positionStep(step) {
  if (!state) return;
  const counter = state.overlay.querySelector('#tourCounter');
  const title = state.overlay.querySelector('#tourTitle');
  const body = state.overlay.querySelector('#tourBody');
  const next = state.overlay.querySelector('#tourNext');
  counter.textContent = `${state.index + 1} of ${state.steps.length}`;
  title.textContent = step.title;
  body.textContent = step.body;
  next.textContent = state.index === state.steps.length - 1 ? 'Done' : 'Next';

  const spotlight = state.overlay.querySelector('#tourSpotlight');
  const card = state.overlay.querySelector('#tourCard');
  const targetEl = step.target ? document.querySelector(step.target) : null;

  if (!targetEl) {
    // No spotlight — center the card.
    spotlight.style.display = 'none';
    card.style.left = '50%';
    card.style.top = '50%';
    card.style.transform = 'translate(-50%, -50%)';
    return;
  }

  // Make sure the target is on screen (skip for fixed bottom-nav).
  const isFixedNav = step.target.includes('TabBtn') && !step.target.includes('libtab') && !step.target.includes('mode');
  if (!isFixedNav) {
    try { targetEl.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch {}
  }

  const rect = targetEl.getBoundingClientRect();
  const pad = 8;
  spotlight.style.display = 'block';
  spotlight.style.left = `${rect.left - pad}px`;
  spotlight.style.top = `${rect.top - pad}px`;
  spotlight.style.width = `${rect.width + pad * 2}px`;
  spotlight.style.height = `${rect.height + pad * 2}px`;

  // Position the card: prefer below the target, fall back to above, clamp
  // into the viewport. Mobile-first: card is fixed-width 280px.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardWidth = Math.min(280, vw - 24);
  const cardHeight = card.offsetHeight || 200;
  const gap = 14;

  let top = rect.bottom + gap;
  if (top + cardHeight > vh - 12) {
    // Not enough room below — try above.
    top = rect.top - gap - cardHeight;
  }
  // Still off-screen? Pin to whichever edge has more room.
  if (top < 12) {
    top = Math.max(12, Math.min(vh - cardHeight - 12, vh / 2 - cardHeight / 2));
  }

  let left = rect.left + rect.width / 2 - cardWidth / 2;
  left = Math.max(12, Math.min(vw - cardWidth - 12, left));

  card.style.left = `${left}px`;
  card.style.top = `${top}px`;
  card.style.transform = 'none';
  card.style.width = `${cardWidth}px`;
}

function onResize() {
  if (!state) return;
  const step = state.steps[state.index];
  if (step) positionStep(step);
}

function next() {
  if (!state) return;
  state.index += 1;
  if (state.index >= state.steps.length) { end(); return; }
  renderStep();
}

function end() {
  if (!state) return;
  state.overlay?.remove();
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', onResize, true);
  state = null;
  markTourCompleted();
}
